import { useFormContext } from 'react-hook-form';
import { type FC, memo, useCallback, useEffect, useMemo } from 'react';
import { FormContainer } from 'components/layout';
import { Box, Typography } from '@mui/material';
import type { GridColDef, GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import type { GridRow } from '../api/getAdSets';
import { ChipInput } from 'components/common';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { useAdSetsData } from '../hooks';
import datadogActions from 'lib/datadog';

const DataGridFooter = ({ message }: { message?: string }) => {
  if (!message) return null;

  return (
    <Box
      sx={(theme) => ({
        p: 2,
        height: 54,
        minHeight: 54,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderTop: `1px solid ${theme.palette.divider}`,
        cursor: 'default',
      })}
    >
      <InfoIcon color="primary" sx={{ fontSize: 21 }} />
      <Typography variant="body2">
        {message}
      </Typography>
    </Box>
  );
};

const DataGridAdSet = () => {
  const { watch, setValue, trigger, formState: { errors } } = useFormContext();
  const selectedAdGroups: string[] = watch('selectedAdGroups') || [];
  const adGroupErrorMessage = errors?.selectedAdGroups?.message;

  const { gridRows, isLoading, isAdGroupRow, isError, error } = useAdSetsData();

  const COLUMNS: GridColDef<GridRow>[] = useMemo(() => [
    {
      field: 'campaign_name',
      headerName: 'Name',
      width: 400,
      flex: 1,
      renderCell: (params: GridRenderCellParams<GridRow>) => {
        if (isAdGroupRow(params.row)) {
          return (
            <Box sx={{ display: 'flex', height: '100%', alignItems: 'center' }}>
              <Typography
                variant="body2"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                {params.row.adgroup_name}
              </Typography>
            </Box>
          );
        }

        return (
          <Box sx={{ display: 'flex', height: '100%', alignItems: 'center' }}>
            <Typography
              variant="body2"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              {params.row.campaign_name}
            </Typography>
          </Box>
        );
      },
    },
  ], [isAdGroupRow]);

  useEffect(() => {
    void trigger('selectedAdGroups');
  }, [trigger]);

  useEffect(() => {
    if (!isError || !error) {
      return;
    }

    datadogActions.trackDatadogError(error as Error, {
      source: 'tiktok_choose_adset_query',
    });
  }, [isError, error]);

  const handleSelectionMode = useCallback(
    (rows: GridRowSelectionModel) => {
      setValue('selectedAdGroups', rows, { shouldValidate: true });
    },
    [setValue],
  );

  return (
    <Box sx={{ flex: 1, height: '100%', width: '100%', position: 'relative' }}>
      <Box style={{ position: 'absolute', left: 0, top: 0, overflow: 'auto', height: '100%', width: '100%' }}>
        <DataGrid
          rows={gridRows}
          columns={COLUMNS}
          loading={isLoading}
          hideFooterPagination
          rowSelectionModel={selectedAdGroups}
          getRowId={(row) => {
            return isAdGroupRow(row)
              ? row.adgroup_id
              : row.campaign_id;
          }}
          getRowClassName={(params) =>
            isAdGroupRow(params.row) ? 'adgroup-row' : 'campaign-row'
          }
          isRowSelectable={(params) => isAdGroupRow(params.row)}
          checkboxSelection
          onRowSelectionModelChange={handleSelectionMode}
          sx={{
            '& .campaign-row': {
              backgroundColor: '#f5f5f5',
              height: 30,
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
              '& .MuiDataGrid-cellCheckbox': {
                display: 'none',
              },
            },
            '& .adgroup-row:hover': {
              backgroundColor: '#fafafa',
            },
          }}
          slots={{
            footer: () => <DataGridFooter message={adGroupErrorMessage as string} />,
          }}
        />
      </Box>
    </Box>
  );
};

const ChooseAdSet: FC = memo(() => {
  const { formState: { errors } } = useFormContext();
  const isCampaignFieldError = errors?.selectedCampaigns?.message as string;

  return (
    <FormContainer
      title={'Choose Campaigns'}
      error={isCampaignFieldError}
      sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flex: 1,
            flexDirection: 'row',
            alignItems: 'start',
            flexWrap: 'nowrap',
          }}
        >
          <ChipInput
            name="filterAdSetsByName"
            inputLabel="Enter adset name"
          />
        </Box>
      </Box>
      <DataGridAdSet />
    </FormContainer>
  );
});
ChooseAdSet.displayName = 'ChooseAdSet';

export default ChooseAdSet;